import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Iconify from '../iconify';
import { signInWithEmail } from '../../service/auth';
import {
  authBackButtonSx,
  authDialogPaperSx,
  authFieldSx,
  authForgotLinkSx,
  authFormSx,
  authPrimaryButtonSx,
} from './modalLayout';

// Sign-in validates only that something was typed. The password policy
// belongs to sign-up: an existing member whose password predates the
// current rules must still be able to get in.
const SignInSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
  password: Yup.string().required('Required'),
});

/**
 * Email/password sign-in.
 *
 * Hands the entire signInWithEmail result to onSignedIn without inspecting
 * it. Whether the member still needs phone verification is SocialLoginModal's
 * call, kept in one place so the social, sign-up and sign-in paths cannot
 * drift apart.
 *
 * @param {string} initialEmail - prefill, e.g. from sign-up hitting
 *   an address that already has an account.
 * @param {(result: {user: Object, requiresPhoneVerification: boolean}) => void} onSignedIn
 * @param {(email: string) => void} onForgotPassword - receives whatever is
 *   currently typed, so it carries into the reset form.
 */
export default function EmailSignInModal({
  open,
  handleClose,
  initialEmail = '',
  onSignedIn,
  onForgotPassword,
  onBack,
}) {
  const [showPassword, setShowPassword] = React.useState(false);
  const emailInputRef = React.useRef(null);

  // Put the caret in the first field on open, as EmailSignUpModal,
  // PhoneVerificationModal and ForgotPasswordModal all do.
  React.useEffect(() => {
    if (open) {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    }
  }, [open]);

  const onSubmit = async (values, { setSubmitting }) => {
    const result = await signInWithEmail(values.email, values.password);
    // signInWithEmail returns null and has already toasted the reason.
    if (result && result.user) {
      onSignedIn(result);
    }
    setSubmitting(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="email-signin-title"
      aria-describedby="email-signin-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-signin-title"
        aria-describedby="email-signin-description"
        data-testid="email-signin-modal"
        sx={authDialogPaperSx}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <IconButton
            onClick={onBack}
            aria-label="Back"
            data-testid="signin-back"
            sx={authBackButtonSx}
          >
            <Iconify icon="eva:arrow-back-fill" width={16} sx={{ color: '#374151' }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          <Typography
            id="email-signin-title"
            sx={{ fontSize: '20px', fontWeight: 700, color: '#111827', textAlign: 'center' }}
          >
            Welcome back
          </Typography>
          <Typography
            id="email-signin-description"
            sx={{ fontSize: '14px', color: '#6B7280', mt: 0.5, mb: 2.5, textAlign: 'center' }}
          >
            Sign in with your email and password.
          </Typography>

          <Formik
            initialValues={{ email: initialEmail, password: '' }}
            enableReinitialize
            validationSchema={SignInSchema}
            onSubmit={onSubmit}
          >
            {({ handleChange, handleSubmit, touched, errors, values, isSubmitting }) => (
              <Box component="form" onSubmit={handleSubmit} noValidate sx={authFormSx}>
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                    Email Address
                  </Typography>
                  <TextField
                    name="email"
                    type="email"
                    placeholder="john.doe@example.com"
                    value={values.email}
                    onChange={handleChange}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    aria-label="Email address"
                    autoComplete="email"
                    inputRef={emailInputRef}
                    inputProps={{ 'data-testid': 'signin-email' }}
                    fullWidth
                    size="small"
                    sx={authFieldSx}
                  />
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                    Password
                  </Typography>
                  <TextField
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Your password"
                    value={values.password}
                    onChange={handleChange}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    aria-label="Password"
                    autoComplete="current-password"
                    inputProps={{ 'data-testid': 'signin-password' }}
                    fullWidth
                    size="small"
                    sx={authFieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            sx={{ width: 36, minWidth: 36, color: '#9CA3AF' }}
                          >
                            <Iconify
                              icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                              width={16}
                              sx={{ color: '#9CA3AF' }}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ textAlign: 'right', mb: 2 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onForgotPassword(values.email)}
                    data-testid="signin-forgot"
                    sx={authForgotLinkSx}
                  >
                    Forgot password?
                  </Box>
                </Box>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  fullWidth
                  data-testid="signin-submit"
                  sx={authPrimaryButtonSx}
                >
                  {isSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Sign in'}
                </Button>
              </Box>
            )}
          </Formik>
        </Box>
      </Box>
    </Modal>
  );
}
